import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  listPosts, createPost, likePost, reportPost, deletePost, moderatePost, reportedPosts,
  listReplies, createReply, uploadFile, absFileUrl,
} from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useLang } from "../../i18n/LanguageContext";
import { toast } from "sonner";
import { Heart, Flag, Trash2, Loader2, Send, ShieldAlert, Eye, EyeOff, Users, ImagePlus, X, MessageCircle, Share2 } from "lucide-react";

const shareLink = async (url, title) => {
  try {
    if (navigator.share) { await navigator.share({ title, url }); return; }
    await navigator.clipboard.writeText(url);
    toast.success("Lien copié dans le presse-papier");
  } catch (e) {
    if (e?.name !== "AbortError") {
      try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); } catch { toast.error("Partage impossible."); }
    }
  }
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR");
};
const initials = (n) => (n || "?").slice(0, 2).toUpperCase();

const Replies = ({ postId }) => {
  const { user, setAuthOpen } = useAuth();
  const [replies, setReplies] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { listReplies(postId).then(({ data }) => setReplies(data)).catch(() => {}); }, [postId]);

  const send = async () => {
    if (!user) return setAuthOpen(true);
    if (!text.trim()) return;
    try {
      setBusy(true);
      const { data } = await createReply(postId, text.trim());
      setReplies((r) => [...r, data]);
      setText("");
    } catch { toast.error("Réponse impossible."); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-blue-500/10 pt-4" data-testid={`replies-${postId}`}>
      <div className="space-y-3">
        {replies.map((r) => (
          <div key={r.id} className="flex gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-400/30 bg-blue-500/10 font-mono text-[9px] text-blue-200">{initials(r.author_name)}</div>
            <div className="rounded-xl border border-blue-500/10 bg-blue-400/[0.02] px-4 py-2.5">
              <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-blue-300/70">{r.author_name} · {timeAgo(r.created_at)}</div>
              <p className="mt-1 font-body text-sm text-[#CBD5E1]">{r.content}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <input
          data-testid={`reply-input-${postId}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Écrire une réponse…"
          className="flex-1 rounded-full border border-blue-500/15 bg-transparent px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40"
        />
        <button data-testid={`reply-send-${postId}`} onClick={send} disabled={busy} className="inline-flex items-center rounded-full bg-blue-600 px-4 py-2.5 text-white disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </div>
    </div>
  );
};

const PostCard = ({ post, onLike, onReport, onDelete, onModerate, isAdmin, adminView, highlighted }) => {
  const [showReplies, setShowReplies] = useState(false);
  return (
    <motion.div layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
      data-testid={`post-${post.id}`}
      className={`rounded-2xl border p-6 backdrop-blur-xl transition-shadow duration-500 ${highlighted ? "border-blue-400/70 bg-blue-500/[0.06] ring-2 ring-blue-400/50" : post.hidden ? "border-red-500/30 bg-red-500/[0.04]" : "border-blue-500/15 bg-blue-400/[0.02]"}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-blue-400/40 bg-blue-500/15 font-mono text-xs text-blue-200">
            {post.author_picture ? <img src={post.author_picture} alt="" className="h-full w-full object-cover" /> : initials(post.author_name)}
          </div>
          <div>
            <div className="font-display text-sm font-700 tracking-tight text-white">{post.author_name}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#8AA0C0]">{timeAgo(post.created_at)}</div>
          </div>
        </div>
        {isAdmin && post.reports > 0 && (
          <span className="flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-red-300">
            <ShieldAlert size={11} /> {post.reports}
          </span>
        )}
      </div>

      {post.content && <p className="mt-4 whitespace-pre-wrap font-body text-sm leading-relaxed text-[#CBD5E1]">{post.content}</p>}
      {post.media && post.media_type === "image" && <img src={post.media} alt="" className="mt-4 max-h-[420px] w-full rounded-xl border border-blue-500/15 object-cover" />}
      {post.media && post.media_type === "video" && <video src={post.media} controls className="mt-4 max-h-[420px] w-full rounded-xl border border-blue-500/15" />}

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button data-testid={`post-like-${post.id}`} onClick={() => onLike(post)}
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-all duration-300 ${post.liked ? "border-blue-400/60 bg-blue-500/20 text-blue-100" : "border-blue-500/20 text-[#CBD5E1] hover:border-blue-400/40 hover:text-white"}`}>
          <Heart size={13} className={post.liked ? "fill-blue-300 text-blue-300" : ""} /> {post.likes}
        </button>
        <button data-testid={`post-reply-toggle-${post.id}`} onClick={() => setShowReplies((s) => !s)}
          className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#CBD5E1] transition-all duration-300 hover:border-blue-400/40 hover:text-white">
          <MessageCircle size={12} /> {post.reply_count || 0}
        </button>
        <button data-testid={`post-report-${post.id}`} onClick={() => onReport(post)} disabled={post.reported}
          className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0] transition-all duration-300 hover:border-orange-400/40 hover:text-orange-300 disabled:opacity-50">
          <Flag size={12} /> {post.reported ? "Signalé" : "Signaler"}
        </button>
        <button data-testid={`post-share-${post.id}`} onClick={() => shareLink(`${window.location.origin}/?post=${post.id}#community`, post.author_name + " · AIBA")}
          className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0] transition-all duration-300 hover:border-blue-400/40 hover:text-blue-200">
          <Share2 size={12} /> Partager
        </button>
        {(post.own || isAdmin) && (
          <button data-testid={`post-delete-${post.id}`} onClick={() => onDelete(post)}
            className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 text-[#8AA0C0] transition-all duration-300 hover:border-red-400/40 hover:text-red-300">
            <Trash2 size={12} />
          </button>
        )}
        {isAdmin && (
          <button data-testid={`post-moderate-${post.id}`} onClick={() => onModerate(post)}
            className="ml-auto inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#8AA0C0] transition-all duration-300 hover:border-blue-400/40 hover:text-blue-200">
            {post.hidden ? <><Eye size={12} /> Rétablir</> : <><EyeOff size={12} /> Masquer</>}
          </button>
        )}
      </div>
      {showReplies && <Replies postId={post.id} />}
    </motion.div>
  );
};

export const Community = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const { user, setAuthOpen } = useAuth();
  const { t } = useLang();
  const isAdmin = user?.role === "admin";
  const fileRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [media, setMedia] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [adminView, setAdminView] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const deepLinkDone = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = adminView && isAdmin ? await reportedPosts() : await listPosts();
      setPosts(data);
    } catch { /* */ }
  }, [adminView, isAdmin]);
  useEffect(() => { load(); }, [load]);

  // Deep link: /?post=<id>#community scrolls to and highlights the shared post.
  useEffect(() => {
    if (deepLinkDone.current || posts.length === 0) return;
    const pid = new URLSearchParams(window.location.search).get("post");
    if (!pid) return;
    deepLinkDone.current = true;
    setHighlightId(pid);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="post-${pid}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 400);
    window.history.replaceState(null, "", window.location.pathname + "#community");
    setTimeout(() => setHighlightId(null), 4000);
  }, [posts]);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 60 * 1024 * 1024) { toast.error("Fichier trop lourd (max 60 Mo)."); return; }
    try {
      setUploading(true);
      const { data } = await uploadFile(f);
      setMedia(absFileUrl(data.path));
      setMediaType(f.type.startsWith("video") ? "video" : "image");
    } catch { toast.error("Upload impossible."); } finally { setUploading(false); }
  };

  const publish = async () => {
    if (!user) return setAuthOpen(true);
    if (!content.trim() && !media) return;
    try {
      setPosting(true);
      const { data } = await createPost(content.trim(), media, mediaType);
      setPosts((p) => [data, ...p]);
      setContent(""); setMedia(null); setMediaType(null);
      toast.success("Publié dans la communauté");
    } catch { toast.error("Échec de la publication."); } finally { setPosting(false); }
  };

  const onLike = async (post) => {
    if (!user) return setAuthOpen(true);
    try { const { data } = await likePost(post.id); setPosts((ps) => ps.map((p) => (p.id === post.id ? data : p))); } catch { /* */ }
  };
  const onReport = async (post) => {
    if (!user) return setAuthOpen(true);
    try { await reportPost(post.id); setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, reported: true, reports: p.reports + 1 } : p))); toast.success("Signalé à l'administrateur"); } catch { /* */ }
  };
  const onDelete = async (post) => {
    try { await deletePost(post.id); setPosts((ps) => ps.filter((p) => p.id !== post.id)); toast.success("Supprimé"); } catch { toast.error("Suppression impossible."); }
  };
  const onModerate = async (post) => {
    try { const { data } = await moderatePost(post.id); if (adminView) setPosts((ps) => ps.map((p) => (p.id === post.id ? { ...p, hidden: data.hidden } : p))); else load(); toast.success(data.hidden ? "Masqué" : "Rétabli"); } catch { /* */ }
  };

  return (
    <section id="community" data-testid="community-section" className="relative py-28 lg:py-36">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(37,99,235,0.08),transparent_55%)]" />
      <div className="relative mx-auto max-w-[900px] px-6 lg:px-10">
        <div ref={ref} className="mb-10">
          <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} className="font-mono text-xs uppercase tracking-[0.35em] text-blue-400">[ {t("community.tag")} ]</motion.span>
          <motion.h2 initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="mt-4 flex items-center gap-4 font-display text-4xl font-800 tracking-tighter text-white sm:text-5xl">
            <Users size={38} className="text-blue-400" /> {t("community.title")}
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.15 }} className="mt-5 font-body text-base leading-relaxed text-[#CBD5E1] sm:text-lg">
            {t("community.subtitle")}
          </motion.p>
          {isAdmin && (
            <button data-testid="community-admin-toggle" onClick={() => setAdminView((v) => !v)}
              className={`mt-5 inline-flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-all duration-300 ${adminView ? "border-red-400/50 bg-red-500/10 text-red-300" : "border-blue-500/20 text-[#CBD5E1] hover:text-white"}`}>
              <ShieldAlert size={13} /> {adminView ? "Vue signalements" : "Voir les signalements"}
            </button>
          )}
        </div>

        <div className="mb-8 rounded-2xl border border-blue-500/20 bg-blue-400/[0.03] p-6 backdrop-blur-xl" data-testid="community-composer">
          <textarea data-testid="community-input" value={content} onChange={(e) => setContent(e.target.value)}
            placeholder={user ? t("community.placeholder") : t("community.loginToPost")} rows={3} maxLength={2000}
            onFocus={() => { if (!user) setAuthOpen(true); }}
            className="w-full resize-none rounded-xl border border-blue-500/15 bg-transparent p-4 font-body text-sm text-white outline-none placeholder:text-[#405060] focus:border-blue-400/40" />
          {media && (
            <div className="relative mt-3 inline-block">
              {mediaType === "video" ? <video src={media} className="max-h-40 rounded-lg" /> : <img src={media} alt="" className="max-h-40 rounded-lg" />}
              <button onClick={() => { setMedia(null); setMediaType(null); }} className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white"><X size={12} /></button>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between">
            <button data-testid="community-media-btn" onClick={() => (user ? fileRef.current?.click() : setAuthOpen(true))} disabled={uploading}
              className="inline-flex items-center gap-2 rounded-full border border-blue-500/20 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200 hover:border-blue-400/40 disabled:opacity-60">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {uploading ? "…" : t("community.media")}
            </button>
            <input ref={fileRef} type="file" accept="image/*,video/*" onChange={pickFile} className="hidden" />
            <button data-testid="community-publish" onClick={publish} disabled={posting}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white cta-glow transition-all duration-300 hover:bg-blue-500 disabled:opacity-60">
              {posting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t("community.publish")}
            </button>
          </div>
        </div>

        <div className="space-y-4" data-testid="community-feed">
          {posts.length === 0 && <p className="py-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-[#405060]">{adminView ? "Aucun signalement" : "Soyez le premier à publier"}</p>}
          <AnimatePresence>
            {posts.map((post) => (
              <PostCard key={post.id} post={post} onLike={onLike} onReport={onReport} onDelete={onDelete} onModerate={onModerate} isAdmin={isAdmin} adminView={adminView} highlighted={highlightId === post.id} />
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
